import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isSameDay } from 'date-fns';
import { useQueryClient } from '@zg-rentals/react-query5';
import { Anchor, Banner, Box, List, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import {
  MESSAGE_LOAD_ERROR_TEXT,
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  NewMessageNotificationBanner,
} from '@zg-rentals/satellite-components';
import {
  SatelliteWebRenterConversationHandler_getMessages_queryOptions,
  useSatelliteWebRenterConversationHandler_getMessages,
  useSatelliteWebRenterConversationHandler_updateStatus,
} from '@zg-rentals/ts-satellite-client-react-query';
import {
  type SatelliteWebRenterConversationGetMessagesResponseDto,
  SatelliteWebRenterConversationHandler_getLatestConversations_Path,
} from '@zg-rentals/ts-satellite-client';
import type { DecoratedMessage } from '../../types/conversationMessages';
import { mapApiMessageToConversationMessage } from '../../utils/mapGetMessagesResponse';
import { AppContext } from '../../contexts/AppContextProvider';
import { getNumberConfig } from '@zg-rentals/frontend-config/client';
import { useSmartPolling, useWebSocketContext, type WebSocketMessage } from '@zg-rentals/web-socket-react';
import { isSatelliteWebSocketEvent } from '@zg-rentals/satellite-web-socket-helpers';
import getMessageTimestamp from '../../utils/getMessageTimestamp';
import stringUtils from '../../utils/stringUtils';
import { ListingAvailability, MessageTypes } from '../../types/conversationMessages';
import MessageItem from '../MessageItem';
import StringConstants from '../../constants/StringConstants';
import AddMessage from '../AddMessage';
import Disclaimer from '../Disclaimer';
import { MessageListContentsSkeleton } from './skeleton';
import viewUtils from '../../utils/viewUtils';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { logError } from '@zg-rentals/log-error';
import NoConversationsContent from '../NoConversationsContent';
import { useRouter } from 'next/router';
import TypingIndicator from '../MessageItem/components/TypingIndicator';
import { WEBSOCKET_DEBUG } from '../../hooks/useWebsocketFeature';
import { bootstrap } from '../../bootstrap';
import type { User } from '../../AuthProvider';
import { conversationIntroTextStyles, messageListThreadStyles, messageListWrapperStyles } from './styles';

const DEFAULT_AI_POLL_INTERVAL = 10;
const TYPING_INDICATOR_TIMEOUT_MS = 90000; // 90 seconds

const logger = bootstrap.logger.child({ name: 'MessageList' });

export type { DecoratedMessage } from '../../types/conversationMessages';

type MessageListProps = {
  user: User;
  shouldAllowSendingAttachmentsOnly: boolean;
  shouldOpenAttachmentsUploader: boolean;
  shouldFetchOlderConversations: boolean;
};

function getListingAvailability(listingDetails: { syndicationUrl?: string } | undefined): ListingAvailability {
  if (!listingDetails) return ListingAvailability.Unknown;
  if (listingDetails.syndicationUrl) return ListingAvailability.Available;
  return ListingAvailability.Offmarket;
}

export default function MessageList({
  user,
  shouldAllowSendingAttachmentsOnly,
  shouldOpenAttachmentsUploader,
  shouldFetchOlderConversations,
}: MessageListProps) {
  const queryClient = useQueryClient();
  const { brand, isImpersonator, paths } = useContext(AppContext);
  const { conversationId, folder, messageJustSentRef } = useContext(InboxPageContext);
  const aiConversationPollIntervalInSeconds = getNumberConfig(
    'aiConversationPollIntervalInSeconds',
    DEFAULT_AI_POLL_INTERVAL,
  );
  const aiConversationPollingInterval = aiConversationPollIntervalInSeconds * 1000;
  const router = useRouter();

  const {
    query: { utm_source },
  } = router;
  let resizeObserver: ResizeObserver | null = null;

  // Refs
  const addMessageContainerRef = useRef<HTMLDivElement>(null);
  const messageListContainerRef = useRef<HTMLDivElement>(null);
  const messageListEndRef = useRef<HTMLDivElement | null>(null);
  const lastMarkedReadRef = useRef<string | null>(null);
  const isMessagesEndInViewport = viewUtils.isElementInViewport(messageListEndRef.current);

  // State
  const [temporaryMessages, setTemporaryMessages] = useState<Array<DecoratedMessage>>([]);
  const [shouldShowLoadingSkeleton, setShouldShowLoadingSkeleton] = useState(true);
  const [shouldShowNewMessageNotification, setShouldShowNewMessageNotification] = useState(false);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [previousPaginatedMessages, setPreviousPaginatedMessages] = useState<Array<DecoratedMessage>>([]);
  const [previousPaginatedMessageCount, setPreviousPaginatedMessageCount] = useState<number | null>(null);
  const [shouldShowTypingIndicator, setShouldShowTypingIndicator] = useState(false);

  const { mutateAsync: updateConversationStatusMutation } = useSatelliteWebRenterConversationHandler_updateStatus({
    meta: {
      context: {
        api: 'updateStatus',
        details: 'MessageList - Error marking conversation as read',
      },
    },
  });

  const { isWebSocketEnabledAndReady } = useWebSocketContext();
  const [isPollingPaused, setIsPollingPaused] = useState(false);

  const {
    data: getMessagesData,
    isLoading: loading,
    error,
    refetch,
    isFetching,
  } = useSatelliteWebRenterConversationHandler_getMessages(
    { conversationId: conversationId || '' },
    {
      enabled: !!conversationId,
      refetchInterval: (query) => {
        const aiDisclaimer = query.state.data?.conversation?.disclaimers?.ai;
        return !!aiDisclaimer && !isWebSocketEnabledAndReady && !isPollingPaused
          ? aiConversationPollingInterval
          : false;
      },
      meta: {
        context: {
          api: 'getMessages',
          details: 'MessageList - Error fetching messages',
        },
      },
    },
  );

  useEffect(() => {
    if (error) {
      logError({
        error,
        context: {
          info: 'Error fetching MessageList messages',
          utmSource: utm_source,
        },
      });
    }
  }, [error, utm_source]);

  const mappedMessages = useMemo(
    () => (getMessagesData?.conversation?.messages ?? []).map(mapApiMessageToConversationMessage),
    [getMessagesData],
  );

  const listingAvailability = getListingAvailability(getMessagesData?.conversation?.listingDetails);

  // Prune temporary messages once the API response confirms them
  useEffect(() => {
    if (!mappedMessages.length) return;
    const apiMessageIds = new Set(mappedMessages.map((m) => m.messageId));
    setTemporaryMessages((prev) => prev.filter((m) => !apiMessageIds.has(m.messageId)));
  }, [mappedMessages]);

  const messages = useMemo(() => {
    const apiMessageIds = new Set(mappedMessages.map((m) => m.messageId));
    const olderMessages = previousPaginatedMessages.filter((m) => !apiMessageIds.has(m.messageId));
    const pendingTempMessages = temporaryMessages.filter((m) => !apiMessageIds.has(m.messageId));
    return [...olderMessages, ...mappedMessages, ...pendingTempMessages] as Array<DecoratedMessage>;
  }, [mappedMessages, temporaryMessages, previousPaginatedMessages]);

  const { acceptsReplies, acceptsRepliesExplanation, disclaimers, hasUnreadMessage, isConversationBlockedByZillow } =
    getMessagesData?.conversation || {};
  const pageMessageCount = Number(getMessagesData?.pageMessageCount);
  const signInPath = paths.signInPage(brand, `${paths.conversationsPage}/${conversationId}`);
  const aiDisclaimer = disclaimers?.ai;
  const shouldShowChatDisabledMessage = !acceptsReplies && !isConversationBlockedByZillow;
  const shouldShowStartConversationText =
    (previousPaginatedMessageCount !== null && previousPaginatedMessageCount < 10) || pageMessageCount < 10;
  // Proxit API returns 401 with { error: { message, data: { loggedIn: false } }, httpStatus: 401 }; ts-client throws with responseStatus: 401. GraphQL used code: 'UNAUTHENTICATED'.
  const err = error as { code?: string; responseStatus?: number } | undefined;
  const errorType = err?.responseStatus === 401 ? StringConstants.GRAPHQL_ERROR_TYPES.UNAUTHENTICATED : err?.code;
  const shouldShowReloginNotification = errorType === StringConstants.GRAPHQL_ERROR_TYPES.UNAUTHENTICATED;
  let prevMessageDateMs: string;
  const lastMessageSent = messages.length > 0 ? messages[messages.length - 1] : null;
  const shouldMarkAsRead = hasUnreadMessage && !lastMessageSent?.isMessageOwner;

  // WebSocket integration
  const { subscribeToMessages, errorRecoveryCount } = useWebSocketContext();

  const pollingEnabled = !!aiDisclaimer && !isWebSocketEnabledAndReady;
  const { updateLastPollTimestamp } = useSmartPolling({
    pollIntervalMs: aiConversationPollingInterval,
    refetch,
    startPolling: () => setIsPollingPaused(false),
    stopPolling: () => setIsPollingPaused(true),
    isEnabled: pollingEnabled,
  });

  const handleWebSocketMessage = useCallback(
    async (message: WebSocketMessage) => {
      const event = isSatelliteWebSocketEvent(message.event) ? message.event : undefined;
      if (event?.type === 'newMessage') {
        const messageConversationId = event.conversationId;

        if (conversationId === messageConversationId) {
          // Skip refetch when this is the echo of the message we just sent;
          // AddMessage already invalidated getMessages.
          if (messageJustSentRef?.current && messageJustSentRef.current.messageId === event.messageId) {
            messageJustSentRef.current = null;
            return;
          }
          if (WEBSOCKET_DEBUG) {
            logger.info('MessageList: Refetching due to new message in current conversation', {
              eventType: event.type,
              conversationId: messageConversationId,
            });
          }
          await refetch();
        }
      }
    },
    [conversationId, messageJustSentRef, refetch],
  );

  useEffect(() => {
    if (!isWebSocketEnabledAndReady) return;
    return subscribeToMessages(handleWebSocketMessage);
  }, [isWebSocketEnabledAndReady, subscribeToMessages, handleWebSocketMessage]);

  const prevErrorRecoveryCountRef = useRef(errorRecoveryCount);
  useEffect(() => {
    if (!isWebSocketEnabledAndReady) return;
    if (errorRecoveryCount !== prevErrorRecoveryCountRef.current) {
      prevErrorRecoveryCountRef.current = errorRecoveryCount;
      refetch();
    }
  }, [isWebSocketEnabledAndReady, errorRecoveryCount, refetch]);

  // Update last poll timestamp when a refetch completes
  const prevFetchingRef = useRef(isFetching);
  useEffect(() => {
    if (prevFetchingRef.current && !isFetching) {
      updateLastPollTimestamp();
    }
    prevFetchingRef.current = isFetching;
  }, [isFetching, updateLastPollTimestamp]);

  // Only show typing indicator for AI Conversations where the user IS the owner of the last message sent.
  // Hide indicator if the last message was sent more than 90 seconds ago
  useEffect(() => {
    if (!lastMessageSent || !lastMessageSent?.messageDateMs) {
      setShouldShowTypingIndicator(false);
      return;
    }

    const timeSinceLastMessageSent = new Date().getTime() - new Date(parseInt(lastMessageSent.messageDateMs)).getTime();
    const isLastMessageRecent = timeSinceLastMessageSent < TYPING_INDICATOR_TIMEOUT_MS;

    const baseShouldShowTypingIndicator =
      !!aiDisclaimer &&
      isLastMessageRecent &&
      !!lastMessageSent.isMessageOwner &&
      lastMessageSent.sendStatus !== MESSAGE_STATUS.FAILED &&
      lastMessageSent.sendStatus !== MESSAGE_STATUS.SENDING;

    setShouldShowTypingIndicator(baseShouldShowTypingIndicator);

    if (baseShouldShowTypingIndicator) {
      const timeoutId = setTimeout(() => {
        setShouldShowTypingIndicator(false);
      }, TYPING_INDICATOR_TIMEOUT_MS);
      return () => clearTimeout(timeoutId);
    }
  }, [aiDisclaimer, lastMessageSent]);

  useEffect(() => {
    /**
     * As the AddMessage text area grows in height, scroll MessageList
     * to the bottom so that the latest message is always in view
     */
    if (window.ResizeObserver && addMessageContainerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        if (messageListContainerRef?.current) {
          messageListContainerRef.current.scrollTo(0, messageListContainerRef.current.scrollHeight);
        }
      });
      resizeObserver.observe(addMessageContainerRef.current);
    }

    // Disconnect on component unmount
    return () => {
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
      if (addMessageContainerRef?.current) {
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
        resizeObserver?.unobserve(addMessageContainerRef.current);
      }
      resizeObserver?.disconnect();
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  useEffect(() => {
    if (shouldShowChatDisabledMessage) {
      const notAcceptingRepliesMessageObject: DecoratedMessage = {
        attachments: [],
        messageType: MessageTypes.NotAcceptingReplies,
        messageDateMs: '',
        isMessageOwner: false,
        message: acceptsRepliesExplanation,
      };
      messages.push(notAcceptingRepliesMessageObject);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [messages]);

  useEffect(() => {
    if (!loading) {
      setShouldShowLoadingSkeleton(false);
    }
  }, [loading]);

  useEffect(() => {
    // Clear state on conversationId change
    setTemporaryMessages([]);
    setPreviousPaginatedMessageCount(null);
    setPreviousPaginatedMessages([]);
    setShouldShowLoadingSkeleton(loading && !getMessagesData);
    lastMarkedReadRef.current = null;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [conversationId]);

  // Handle scrolling and notifications for various scenarios
  useEffect(() => {
    if (shouldShowLoadingSkeleton) return;

    // Scenario 1: ConversationId changed - scroll to bottom
    if (conversationId) {
      return shouldMarkAsRead ? scrollToBottomAndMarkAsRead() : scrollToBottom();
    }
  }, [conversationId, shouldShowLoadingSkeleton]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark conversation as read when a background refetch reveals unread messages (e.g. after
  // switching to a conversation whose cache was invalidated by a WebSocket event).
  // The conversationId effect above runs before the refetch completes, so shouldMarkAsRead
  // is false at that point. This effect catches the hasUnreadMessage transition to true.
  // Only marks as read when the user is at the bottom of the list — if scrolled up, the
  // messageCount/lastMessageId effect will show the "new messages" banner instead.
  useEffect(() => {
    if (shouldMarkAsRead && !shouldShowLoadingSkeleton && isMessagesEndInViewport) {
      markConversationAsRead();
    }
  }, [shouldMarkAsRead, shouldShowLoadingSkeleton]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use stable primitives so this effect only runs when the message list actually changes (e.g. new
  // message arrived). Using `messages` caused an infinite loop because messages is a new array
  // reference every render.
  const messageCount = messages.length;
  const lastMessageId = messages[messageCount - 1]?.messageId;

  useEffect(() => {
    if (isLoadingMoreMessages || loading) return;

    // Scenario 2: New unread message and user is not at bottom - show notification
    if (!isMessagesEndInViewport && hasUnreadMessage) {
      setShouldShowNewMessageNotification(true);
      return;
    }

    // Scenario 3: New message added (sent or received) and user is at bottom - scroll to bottom
    if (isMessagesEndInViewport) {
      return shouldMarkAsRead ? scrollToBottomAndMarkAsRead() : scrollToBottom();
    }
  }, [messageCount, lastMessageId, isLoadingMoreMessages, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Scenario 4: An optimistic message or typing indicator is displayed - scroll to bottom
    scrollToBottom();
  }, [temporaryMessages, shouldShowTypingIndicator]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevMessageCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (!getMessagesData?.conversation) return;
    const currentCount = mappedMessages.length;

    // On first data load, just set the ref and skip effect
    if (prevMessageCountRef.current === null) {
      prevMessageCountRef.current = currentCount;
      return;
    }

    if (currentCount > prevMessageCountRef.current) {
      // Only invalidate when the new message is from the other party (not us).
      // When we send a message, AddMessage already invalidates; skip here to avoid duplicate API calls.
      const lastMessage = mappedMessages[currentCount - 1];
      const newMessageIsFromCurrentUser = lastMessage?.isMessageOwner === true;
      if (!newMessageIsFromCurrentUser) {
        // New message detected from other party (e.g. WebSocket or poll); refetch conversation list
        // so the sidebar shows unread dot, updated preview, and correct hasUnreadMessage.
        // Use refetchQueries to ensure the getLatestConversations request is made and UI updates.
        queryClient.refetchQueries({
          predicate: (query) =>
            query.queryKey[0] === SatelliteWebRenterConversationHandler_getLatestConversations_Path &&
            (query.queryKey[1] as { folder?: string })?.folder === folder,
        });
      }
    }

    prevMessageCountRef.current = currentCount;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [mappedMessages, folder, queryClient]);

  const handleLoadMoreMessages = async () => {
    const firstMessageId = messages[0]?.messageId;
    if (!conversationId || !firstMessageId) return;
    try {
      setIsLoadingMoreMessages(true);
      const olderPage = await queryClient.fetchQuery(
        SatelliteWebRenterConversationHandler_getMessages_queryOptions({
          conversationId,
          beforeMessageId: firstMessageId,
        }),
      );
      const newMessages = (olderPage.conversation.messages ?? []).map(mapApiMessageToConversationMessage);
      const updatedMessages = [...newMessages, ...messages];
      setPreviousPaginatedMessages(updatedMessages);
      setPreviousPaginatedMessageCount(newMessages.length);
    } catch (err) {
      logError({ error: err, context: 'Error fetching more messages' });
    } finally {
      setIsLoadingMoreMessages(false);
    }
  };

  function scrollToBottom() {
    if (shouldShowNewMessageNotification) {
      setShouldShowNewMessageNotification(false);
    }

    if (messageListEndRef.current) {
      messageListEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'end',
      });
    }
  }

  function scrollToBottomAndMarkAsRead() {
    scrollToBottom();
    markConversationAsRead();
  }

  async function markConversationAsRead() {
    const lastReadMs = messages[messages.length - 1].messageDateMs;

    if (isImpersonator || !conversationId || !lastReadMs) return;

    // Dedup: multiple effects can trigger mark-as-read in the same render cycle.
    // Skip if we already marked this conversation with the same lastReadMs.
    const dedupKey = `${conversationId}:${lastReadMs}`;
    if (lastMarkedReadRef.current === dedupKey) return;
    lastMarkedReadRef.current = dedupKey;

    try {
      await updateConversationStatusMutation(
        {
          postData: {
            conversationId,
            lastReadMs: Number(lastReadMs),
          },
        },
        {
          onSuccess: () => {
            // Update conversation list cache so sidebar unread dot clears without a second getLatestConversations call.
            queryClient.setQueriesData(
              {
                predicate: (query) =>
                  query.queryKey[0] === SatelliteWebRenterConversationHandler_getLatestConversations_Path &&
                  (query.queryKey[1] as { folder?: string })?.folder === folder,
              },
              (
                data:
                  | {
                      pages?: Array<{ conversations?: Array<{ conversationId?: string; hasUnreadMessage?: boolean }> }>;
                      pageParams?: Array<unknown>;
                    }
                  | undefined,
              ) => {
                if (!data?.pages) return data;
                const conversationIdStr = String(conversationId);
                return {
                  ...data,
                  pages: data.pages.map((page) => ({
                    ...page,
                    conversations: (page.conversations ?? []).map((conv) =>
                      String(conv.conversationId) === conversationIdStr ? { ...conv, hasUnreadMessage: false } : conv,
                    ),
                  })),
                };
              },
            );

            // Update the message list (getMessages) cache to mark this conversation as read
            queryClient.setQueryData<SatelliteWebRenterConversationGetMessagesResponseDto>(
              useSatelliteWebRenterConversationHandler_getMessages.getKey({ conversationId }),
              (prev) => {
                if (!prev?.conversation) return prev;
                return {
                  ...prev,
                  conversation: {
                    ...prev.conversation,
                    hasUnreadMessage: false,
                  },
                };
              },
            );
          },
        },
      );
    } catch (error) {
      logError({ error, context: 'Error marking conversation as read' });
    }
  }

  const handleNotificationCloseClick = () => setShouldShowNewMessageNotification(false);

  const updateTemporaryMessage = (temporaryMessageKey: string, updates: Partial<DecoratedMessage>): void => {
    setTemporaryMessages((prevTemporaryMessages) => {
      const index = prevTemporaryMessages.findIndex(
        (temporaryMessage) => temporaryMessage.temporaryMessageKey === temporaryMessageKey,
      );

      if (index === -1) {
        return prevTemporaryMessages;
      }

      const updatedTemporaryMessages = [...prevTemporaryMessages];
      updatedTemporaryMessages[index] = {
        ...updatedTemporaryMessages[index],
        ...updates,
      };

      return updatedTemporaryMessages;
    });
  };

  const handleAddMessage = (optimisticMessage: DecoratedMessage) => {
    const lastTempMessage = temporaryMessages[temporaryMessages.length - 1];
    const isSameTemporaryKey = lastTempMessage?.temporaryMessageKey === optimisticMessage.temporaryMessageKey;

    if (lastTempMessage && isSameTemporaryKey) {
      const updatedMessageText = `${lastTempMessage.message}\n${optimisticMessage.message}`;
      const updatedTemporaryMessages = [...temporaryMessages];
      updatedTemporaryMessages[temporaryMessages.length - 1].message = updatedMessageText;

      setTemporaryMessages(updatedTemporaryMessages);
    } else {
      setTemporaryMessages([...temporaryMessages, optimisticMessage]);
    }
  };

  return (
    <Box css={messageListWrapperStyles}>
      {error != null && !getMessagesData ? (
        <NoConversationsContent user={user} errorCode={errorType} showErrorExperience={true} />
      ) : null}

      {shouldShowNewMessageNotification && (
        <NewMessageNotificationBanner
          onActionClick={scrollToBottomAndMarkAsRead}
          onClose={handleNotificationCloseClick}
        />
      )}

      {shouldShowReloginNotification && (
        <Banner.Root tone="critical">
          <Banner.Body>
            Your session has expired, please{' '}
            <Anchor href={signInPath} onImpact>
              login
            </Anchor>{' '}
            to restore your session.
          </Banner.Body>
        </Banner.Root>
      )}

      <Box css={messageListThreadStyles} ref={messageListContainerRef}>
        {shouldShowLoadingSkeleton ? (
          <MessageListContentsSkeleton />
        ) : (
          <>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                marginBlock: 'default',
              }}
            >
              {shouldShowStartConversationText ? (
                <Box css={conversationIntroTextStyles}>
                  <Paragraph textStyle="body-sm-bold" css={{ marginBottom: 'tighter' }}>
                    {StringConstants.START_CONVERSATION_TEXT}
                  </Paragraph>

                  <Paragraph textStyle="body-xs" css={{ textAlign: 'center', maxWidth: '700px' }}>
                    {StringConstants.WARNING_MESSAGE.text}{' '}
                    <Anchor href={StringConstants.WARNING_MESSAGE.url} target="_blank">
                      Read this article
                    </Anchor>{' '}
                    for more tips on avoiding scams.
                  </Paragraph>

                  {aiDisclaimer && (
                    <Disclaimer
                      disclaimerText={aiDisclaimer.disclaimerText}
                      links={aiDisclaimer.links}
                      emailLinks={aiDisclaimer.emailLinks}
                    />
                  )}
                </Box>
              ) : (
                <TextButton onClick={handleLoadMoreMessages} disabled={isLoadingMoreMessages}>
                  {isLoadingMoreMessages ? 'Loading more messages...' : 'Load more messages'}
                </TextButton>
              )}
            </Box>

            <List.Root appearance="none" data-testid="message-list" css={{ marginBottom: 'loose' }}>
              {messages.map((conversationMessage, idx) => {
                const isLastMessageInList = idx === messages.length - 1;
                const { attachments, message, messageDateMs, messageId, sendStatus } = conversationMessage;
                const messageIsSameDayAsPrevious = prevMessageDateMs && isSameDay(+messageDateMs, +prevMessageDateMs);
                const timestamp =
                  messageIsSameDayAsPrevious || !messageDateMs ? null : getMessageTimestamp(messageDateMs);
                prevMessageDateMs = messageDateMs;

                // Edge case: message objects with missing message AND attachments fields - return "failed to load" item
                if (!message && !attachments) {
                  const failedToLoadMessage = {
                    ...conversationMessage,
                    message: MESSAGE_LOAD_ERROR_TEXT,
                    messageType: MESSAGE_TYPES.SYSTEM_MESSAGE.FAILED_TO_LOAD,
                    uiTreatmentType: MESSAGE_TYPES.LOW_PRIORITY,
                  };

                  return (
                    <MessageItem
                      key={messageId}
                      brand={brand}
                      conversationId={conversationId || ''}
                      isLastMessageInList={isLastMessageInList}
                      listingAvailability={listingAvailability}
                      message={failedToLoadMessage}
                      messageKey={messageId!}
                      selectedFolder={folder}
                      sendStatus={sendStatus}
                      shouldFetchOlderConversations={shouldFetchOlderConversations}
                      updateTemporaryMessage={updateTemporaryMessage}
                    />
                  );
                }

                // Edge case: message field with no text AND no attachments - do not render
                if (
                  stringUtils.isEmptyString(conversationMessage.message || '') &&
                  conversationMessage.attachments.length === 0
                ) {
                  return null;
                }

                return (
                  <React.Fragment key={messageId}>
                    {timestamp && (
                      <Text
                        textStyle="body-xs"
                        css={{
                          display: 'block',
                          color: 'text.subtle',
                          marginBlock: 'default',
                          textAlign: 'center',
                        }}
                        asChild
                      >
                        <time>{timestamp}</time>
                      </Text>
                    )}

                    <MessageItem
                      brand={brand}
                      conversationId={conversationId || ''}
                      isLastMessageInList={isLastMessageInList}
                      listingAvailability={listingAvailability}
                      message={conversationMessage}
                      messageKey={messageId!}
                      selectedFolder={folder}
                      sendStatus={sendStatus}
                      shouldFetchOlderConversations={shouldFetchOlderConversations}
                      updateTemporaryMessage={updateTemporaryMessage}
                    />
                  </React.Fragment>
                );
              })}
            </List.Root>

            {shouldShowTypingIndicator && <TypingIndicator isAiEnabledConversation={!!aiDisclaimer} />}

            <div ref={messageListEndRef} />
          </>
        )}
      </Box>

      {acceptsReplies && (
        <AddMessage
          ref={addMessageContainerRef}
          user={user}
          handleAddMessage={handleAddMessage}
          isConversationBlockedByZillow={isConversationBlockedByZillow || false}
          shouldAllowSendingAttachmentsOnly={shouldAllowSendingAttachmentsOnly}
          shouldOpenAttachmentsUploader={shouldOpenAttachmentsUploader}
          shouldFetchOlderConversations={shouldFetchOlderConversations}
          updateTemporaryMessage={updateTemporaryMessage}
        />
      )}
    </Box>
  );
}
